import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Button } from "reactstrap";
import { Container, Draggable } from "react-smooth-dnd";
import { arrayMoveImmutable } from "array-move";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import Switch from "react-switch";
import SweetAlert from "sweetalert2";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
  getProductSignatureDetailsApi,
  addProductSignatureDetailApi,
  updateProductSignatureDetailApi,
  toggleProductSignatureDetailActiveApi,
  deleteProductSignatureDetailApi,
  reorderProductSignatureDetailsApi,
} from "../../../../services/manage-product/productService";

const EMPTY_FORM = {
  title: "",
  short_description: "",
  description: "",
  media_type: "image",
  mediaFile: null,
  thumbnailFile: null,
  iconFile: null,
};

const sortByPosition = (rows) =>
  [...rows].sort((a, b) => (a.display_order - b.display_order) || (a.id - b.id));

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

const buildSignatureDetailFormData = (productId, values) => {
  const data = new FormData();
  data.append("product_id", productId);
  data.append("title", values.title.trim());
  data.append("short_description", values.short_description || "");
  data.append("description", values.description || "");
  data.append("media_type", values.media_type);
  if (values.mediaFile) data.append("media_file", values.mediaFile);
  if (values.thumbnailFile) data.append("thumbnail", values.thumbnailFile);
  if (values.iconFile) data.append("icon", values.iconFile);
  data.append("display_order", values.display_order ?? 0);
  data.append("status", values.status ?? 1);
  return data;
};

const ManageSignatureDetailsModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [mutating, setMutating] = useState(false);
  const [reordering, setReordering] = useState(false);

  // A reorder persist and any other mutation must never overlap — both would race to write
  // display_order/other fields for the same scoped product, so one combined flag gates both
  // drag and every other Save/dispatch control.
  const busy = mutating || reordering;

  // null | "add" | { editId }
  const [formMode, setFormMode] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  const fetchEntries = (silent = false) => {
    if (!productId) return;
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    getProductSignatureDetailsApi(productId)
      .then(({ response }) => {
        setEntries(sortByPosition(Array.isArray(response) ? response : []));
      })
      .catch((err) => {
        // A silent (post-mutation) reconcile failing shouldn't blow away the optimistic
        // state the user already sees — only surface the retry UI for the initial load.
        if (!silent) {
          setLoadError(err?.response?.data?.message || err?.message || "Failed to load signature details.");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    // Fully clear stale entries/product scope so a later reopen never flashes a previous
    // product's rows, whether we're closing or switching to a different productId.
    setEntries([]);
    setLoadError("");
    setLoading(false);
    setFormMode(null);
    setFormValues(EMPTY_FORM);
    setFormError("");

    if (open && productId) {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const openAddForm = () => {
    setFormValues(EMPTY_FORM);
    setFormError("");
    setFormMode("add");
  };

  const openEditForm = (row) => {
    setFormValues({
      title: row.title || "",
      short_description: row.short_description || "",
      description: row.description || "",
      media_type: row.media_type,
      mediaFile: null,
      thumbnailFile: null,
      iconFile: null,
    });
    setFormError("");
    setFormMode({ editId: row.id });
  };

  const closeForm = () => {
    setFormMode(null);
    setFormValues(EMPTY_FORM);
    setFormError("");
  };

  const isEditing = formMode && typeof formMode === "object";

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const title = formValues.title.trim();
    if (!title) {
      setFormError("Title is required.");
      return;
    }
    if (!isEditing && !formValues.mediaFile) {
      setFormError("Please choose a media file.");
      return;
    }
    if (!isEditing && formValues.media_type === "video" && !formValues.thumbnailFile) {
      setFormError("A thumbnail is required for video.");
      return;
    }
    setFormError("");
    setMutating(true);

    if (isEditing) {
      const current = entries.find((e) => e.id === formMode.editId);
      const data = buildSignatureDetailFormData(productId, {
        ...formValues,
        title,
        display_order: current?.display_order,
        status: current?.status,
      });
      updateProductSignatureDetailApi(formMode.editId, productId, data)
        .then(({ response }) => {
          setEntries((prev) => sortByPosition(prev.map((e) => (e.id === formMode.editId ? response : e))));
          toast.success("Signature detail updated.");
          closeForm();
          fetchEntries(true);
        })
        .catch((err) => notifyMutationError(err, "Failed to update the signature detail."))
        .finally(() => setMutating(false));
    } else {
      const nextPosition = entries.length ? Math.max(...entries.map((e) => e.display_order)) + 1 : 0;
      const data = buildSignatureDetailFormData(productId, { ...formValues, title, display_order: nextPosition, status: 1 });
      addProductSignatureDetailApi(productId, data)
        .then(({ response }) => {
          setEntries((prev) => sortByPosition([...prev, response]));
          toast.success("Signature detail added.");
          closeForm();
          fetchEntries(true);
        })
        .catch((err) => notifyMutationError(err, "Failed to add the signature detail."))
        .finally(() => setMutating(false));
    }
  };

  const handleToggleActive = (row) => {
    const previousStatus = row.status;
    const nextStatus = previousStatus ? 0 : 1;

    // Optimistic, no confirmation — flip immediately, revert only if the request fails.
    setEntries((prev) => prev.map((e) => (e.id === row.id ? { ...e, status: nextStatus } : e)));
    setMutating(true);

    const data = buildSignatureDetailFormData(productId, {
      title: row.title,
      short_description: row.short_description,
      description: row.description,
      media_type: row.media_type,
      display_order: row.display_order,
      status: nextStatus,
    });

    toggleProductSignatureDetailActiveApi(row.id, productId, data)
      .then(({ response }) => {
        setEntries((prev) => sortByPosition(prev.map((e) => (e.id === row.id ? response : e))));
        fetchEntries(true);
      })
      .catch((err) => {
        setEntries((prev) => prev.map((e) => (e.id === row.id ? { ...e, status: previousStatus } : e)));
        notifyMutationError(err, "Failed to update the active state.");
      })
      .finally(() => setMutating(false));
  };

  const handleDelete = (row) => {
    SweetAlert.fire({
      title: "Delete signature detail?",
      html: `This will permanently remove <b>${row.title}</b>.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setMutating(true);
      deleteProductSignatureDetailApi(row.id)
        .then(() => {
          setEntries((prev) => prev.filter((e) => e.id !== row.id));
          toast.success("Signature detail deleted.");
          fetchEntries(true);
        })
        .catch((err) => {
          if (err?.isAxiosError && err.response?.status === 404) {
            // Already removed (e.g. by another editor) — drop it locally, no error toast.
            setEntries((prev) => prev.filter((e) => e.id !== row.id));
            return;
          }
          notifyMutationError(err, "Failed to delete the signature detail.");
        })
        .finally(() => setMutating(false));
    });
  };

  const handleReorderDrop = ({ removedIndex, addedIndex }) => {
    // Guard even though the handle is visually/pointer-disabled while busy — belt and
    // braces against any stray drop event, and against no-op drops.
    if (busy || removedIndex === null || addedIndex === null || removedIndex === addedIndex) return;

    const lastKnownGoodOrder = entries;
    const reordered = arrayMoveImmutable(entries, removedIndex, addedIndex).map((row, index) => ({
      ...row,
      display_order: index,
    }));

    setEntries(reordered);
    setReordering(true);

    const items = reordered.map((row) => ({ id: row.id, display_order: row.display_order }));

    reorderProductSignatureDetailsApi(productId, items)
      .then(({ response }) => {
        // The server's persisted rows are the new source of truth (survives close/reopen
        // and reload on their own merit — this just keeps the in-memory view in sync now).
        setEntries(sortByPosition(Array.isArray(response) ? response : reordered));
      })
      .catch((err) => {
        setEntries(lastKnownGoodOrder);
        notifyMutationError(err, "Failed to save the new order.");
      })
      .finally(() => setReordering(false));
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="lg">
      <ModalHeader toggle={onClose} tag="div">
        <div>Manage Signature Details</div>
        {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
          <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
            {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </ModalHeader>
      <ModalBody style={{ minHeight: 200, position: "relative" }}>
        {loading && <PreLoader withoutText />}

        {!loading && loadError && (
          <div className="alert alert-danger">
            <div className="font-weight-bold mb-1">Signature details could not be loaded</div>
            <div className="mb-2">{loadError}</div>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchEntries()}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && (
          <>
            {entries.length > 0 && (
              <div style={{ maxHeight: 360, overflowY: "auto", marginBottom: 16 }}>
                <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={handleReorderDrop}>
                  {entries.map((row) => (
                    <Draggable key={row.id}>
                      <div
                        className="d-flex align-items-center border-bottom py-2"
                        style={{ opacity: row.status ? 1 : 0.5, background: "#fff" }}
                      >
                        <span
                          className="drag-handle"
                          style={{
                            cursor: busy ? "not-allowed" : "grab",
                            pointerEvents: busy ? "none" : "auto",
                            opacity: busy ? 0.4 : 1,
                            marginRight: 8,
                            flexShrink: 0,
                          }}
                        >
                          <DragHandleIcon />
                        </span>
                        <img
                          src={row.thumbnail_url}
                          alt=""
                          style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, marginRight: 12, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              wordBreak: "break-word",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {row.title}
                          </div>
                          <small className="text-muted text-capitalize">
                            {row.media_type} · {row.status ? "Active" : "Inactive"}
                          </small>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: 12, marginLeft: 12, flexShrink: 0 }}>
                          <Switch
                            onChange={() => handleToggleActive(row)}
                            checked={Boolean(row.status)}
                            disabled={busy}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            height={20}
                            width={35}
                          />
                          <Button color="link" className="p-0" disabled={busy} onClick={() => openEditForm(row)}>
                            <i className="icon-pencil" />
                          </Button>
                          <Button color="link" className="p-0 text-danger" disabled={busy} onClick={() => handleDelete(row)}>
                            <i className="icon-trash" />
                          </Button>
                        </div>
                      </div>
                    </Draggable>
                  ))}
                </Container>
              </div>
            )}

            {formMode ? (
              <Form onSubmit={handleFormSubmit}>
                <FormGroup>
                  <Label>Title</Label>
                  <Input
                    type="text"
                    value={formValues.title}
                    onChange={(e) => setFormValues((v) => ({ ...v, title: e.target.value }))}
                    invalid={Boolean(formError) && formError.toLowerCase().includes("title")}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Short Description</Label>
                  <Input
                    type="text"
                    value={formValues.short_description}
                    onChange={(e) => setFormValues((v) => ({ ...v, short_description: e.target.value }))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={4}
                    value={formValues.description}
                    onChange={(e) => setFormValues((v) => ({ ...v, description: e.target.value }))}
                  />
                </FormGroup>
                <FormGroup tag="fieldset">
                  <Label>Media Type</Label>
                  <div>
                    {["image", "video"].map((type) => (
                      <FormGroup check inline key={type}>
                        <Label check>
                          <Input
                            type="radio"
                            name="media_type"
                            checked={formValues.media_type === type}
                            onChange={() => setFormValues((v) => ({ ...v, media_type: type }))}
                          />{" "}
                          {type === "image" ? "Image" : "Video"}
                        </Label>
                      </FormGroup>
                    ))}
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Media File {isEditing && <small className="text-muted">(leave blank to keep the current file)</small>}</Label>
                  <Input
                    type="file"
                    onChange={(e) => setFormValues((v) => ({ ...v, mediaFile: e.target.files[0] || null }))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>
                    Thumbnail{" "}
                    <small className="text-muted">
                      {formValues.media_type === "video" && !isEditing ? "(required)" : "(optional)"}
                    </small>
                  </Label>
                  <Input
                    type="file"
                    onChange={(e) => setFormValues((v) => ({ ...v, thumbnailFile: e.target.files[0] || null }))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>
                    Icon <small className="text-muted">(optional)</small>
                  </Label>
                  <Input
                    type="file"
                    onChange={(e) => setFormValues((v) => ({ ...v, iconFile: e.target.files[0] || null }))}
                  />
                </FormGroup>
                {formError && <div className="text-danger small mb-2">{formError}</div>}
                <div className="d-flex" style={{ gap: 8 }}>
                  <Button color="primary" type="submit" disabled={busy}>
                    Save
                  </Button>
                  <Button color="secondary" type="button" outline onClick={closeForm}>
                    Cancel
                  </Button>
                </div>
              </Form>
            ) : (
              <Button color="primary" disabled={busy} onClick={openAddForm}>
                + Add Signature Detail
              </Button>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ManageSignatureDetailsModal;
